import { Suspense, useEffect, useState, useMemo } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard, Users, ShieldCheck, Download, Trophy, Receipt,
    LogOut, Menu, X, Settings, KeySquare, ClipboardList, FileText, Award,
    Activity as ActivityIcon, Landmark, RotateCcw, Cloud, Table2, Calculator,
    ShieldAlert, Images, Scale, Eye, MapPin,
} from 'lucide-react';
import { useAuth } from '../store/auth';
import api from '../utils/api';
import AgeonLogo from '../components/AgeonLogo';
import NotificationBell from '../components/NotificationBell';
import ProfileDropdown from '../components/ProfileDropdown';
import PageLoader from '../components/PageLoader';
import { canAccessPage, canSeeBdmOnlyPage, effectivePermissions } from '../utils/workspace';


const ADMIN_GROUPS = [
    {
        group: 'Overview', items: [
            { key: 'admin.dashboard', to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
            { key: 'admin.kpi-analytics', to: '/admin/kpi-analytics', label: 'KPI analytics', icon: Table2 },
        ]
    },
    {
        group: 'People', items: [
            { key: 'admin.approvals', to: '/admin/approvals', label: 'Approvals', icon: ShieldCheck },
            { key: 'admin.users', to: '/admin/users', label: 'Users', icon: Users },
            { key: 'admin.access', to: '/admin/access', label: 'Access & permissions', icon: KeySquare },
        ]
    },
    {
        group: 'Ageon Clients', items: [
            { key: 'admin.clients', to: '/admin/clients', label: 'All clients', icon: ClipboardList },
            { key: 'admin.payment-ledger', to: '/admin/payment-ledger', label: 'Payment ledger', icon: Landmark },
        ]
    },
    {
        group: 'Content', items: [
            { key: 'admin.form-builder', to: '/admin/form-builder', label: 'Form builder', icon: FileText },
            { key: 'admin.downloads', to: '/admin/downloads', label: 'Downloads', icon: Download },
            { key: 'admin.achievers', to: '/admin/achievers', label: 'Banner Update', icon: Award },
            { key: 'admin.hero-slides', to: '/admin/hero-slides', label: 'Hero Slides', icon: Images },
            { key: 'admin.franchises', to: '/admin/franchises', label: 'Franchise manager', icon: MapPin },
        ]
    },
    {
        group: 'Operations', items: [
            { key: 'admin.incentives', to: '/admin/incentives', label: 'Incentives', icon: Trophy },
            { key: 'admin.refunds', to: '/admin/refunds', label: 'Refunds', icon: RotateCcw },
            { key: 'admin.reimbursements', to: '/admin/reimbursements', label: 'Reimbursements', icon: Receipt },
        ]
    },
    {
        group: 'CRM', items: [
            { key: 'admin.zoho-leads', to: '/admin/zoho-leads', label: 'Zoho Leads', icon: Cloud },
        ]
    },
    {
        group: 'Strategy', items: [
            { key: 'admin.aoi-calculator', to: '/admin/aoi-calculator', label: 'AOI Calculator', icon: Calculator },
            { key: 'admin.aoi-location', to: '/admin/aoi-location', label: 'AOI Location', icon: MapPin },
        ]
    },
    {
        group: 'System', items: [
            { key: 'admin.activity', to: '/admin/activity', label: 'Activity log', icon: ActivityIcon },
            { key: 'admin.settings', to: '/admin/settings', label: 'Settings', icon: Settings },
        ]
    },
];
/* Legacy: /admin/permissions and /admin/roles redirect into /admin/access. */
const ADMIN_NAV = ADMIN_GROUPS.flatMap(g => g.items);

const SALES_GROUPS = [
    {
        group: 'Workspace', items: [
            { key: 'sales.dashboard', to: '/sales/dashboard', label: 'Dashboard', icon: LayoutDashboard },
            { key: 'sales.kpi-analytics', to: '/sales/kpi-analytics', label: 'KPI analytics', icon: Table2 },
            { key: 'sales.leads', to: '/sales/leads', label: 'My leads', icon: Users },
            { key: 'sales.team-leads', to: '/sales/team-leads', label: 'Team leads', icon: Users, bdmOnly: true },
            { key: 'sales.my-team', to: '/sales/my-team', label: 'My Team', icon: Users, bdmOnly: true },
            { key: 'sales.clients', to: '/sales/clients', label: 'My clients', icon: ClipboardList },
            { key: 'sales.legal', to: '/sales/legal', label: 'Legal', icon: Scale },
        ]
    },
    {
        group: 'Resources', items: [
            { key: 'sales.downloads', to: '/sales/downloads', label: 'Downloads', icon: Download },
        ]
    },
    {
        group: 'Earnings', items: [
            { key: 'sales.incentives', to: '/sales/incentives', label: 'Incentives', icon: Trophy },
            { key: 'sales.reimbursements', to: '/sales/reimbursements', label: 'Reimbursements', icon: Receipt },
        ]
    },
];
const SALES_NAV = SALES_GROUPS.flatMap(g => g.items);

export default function AppLayout({ variant = 'admin' }) {
    const { user, logout, loadMe, stopImpersonation, isImpersonating } = useAuth();
    const [openMobile, setOpenMobile] = useState(false);
    const [roleDoc, setRoleDoc] = useState(null);
    const loc = useLocation();
    const nav = useNavigate();

    useEffect(() => { if (!user) loadMe().then(u => { if (!u) nav('/login'); }); /* eslint-disable-next-line */ }, []);
    useEffect(() => { setOpenMobile(false); }, [loc.pathname]);

    /* Pull the user's Role doc so we can fall back to role.permissions when
       the user has no per-user override. Cached for 60s in memory. */
    useEffect(() => {
        if (!user?.role) return;
        api.get('/roles').then(r => {
            setRoleDoc((r.data.items || []).find(x => x.slug === user.role) || null);
        }).catch(() => { });
    }, [user?.role, (user?.permissions || []).join('|'), loc.pathname]);

    /* Staff on the admin layout may also have sales.* role grants — show both sidebars. */
    const { items, groups } = useMemo(() => {
        if (variant !== 'admin') {
            return { items: SALES_NAV, groups: SALES_GROUPS };
        }
        const perms = effectivePermissions(user, roleDoc);
        const includeSales = perms.some(p => p.startsWith('sales.'));
        if (!includeSales) {
            return { items: ADMIN_NAV, groups: ADMIN_GROUPS };
        }
        return {
            items: [...ADMIN_NAV, ...SALES_NAV],
            groups: [...ADMIN_GROUPS, ...SALES_GROUPS],
        };
    }, [variant, user, roleDoc]);

    const visible = useMemo(() => items.filter((i) => {
        if (i.bdmOnly) return canSeeBdmOnlyPage(user, roleDoc, i.key);
        return canAccessPage(user, roleDoc, i.key);
    }), [user, roleDoc, items]);

    return (
        <div className="h-screen flex overflow-hidden bg-ink-50">
            {openMobile && (
                <button
                    type="button"
                    className="fixed inset-0 z-20 bg-ink-900/40 lg:hidden"
                    aria-label="Close menu"
                    onClick={() => setOpenMobile(false)}
                />
            )}
            <aside className={`fixed lg:static inset-y-0 left-0 z-30 flex h-screen w-64 flex-col bg-white border-r border-ink-100
                               transform transition-transform duration-200
                               ${openMobile ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
                <div className="h-16 shrink-0 px-5 flex items-center border-b border-ink-100">
                    <AgeonLogo />
                </div>
                <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-4">
                    {groups.map(g => {
                        const groupItems = g.items.filter(it => visible.some(v => v.key === it.key));
                        if (groupItems.length === 0) return null;
                        return (
                            <div key={g.group}>
                                <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500 font-bold px-3 mb-1.5">
                                    {g.group}
                                </div>
                                <div className="space-y-0.5">
                                    {groupItems.map(item => (
                                        <NavLink key={item.to} to={item.to} end={item.to === '/admin/clients'}
                                            className={({ isActive }) =>
                                                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition
                                                ${isActive ? 'bg-brand-teal-50 text-brand-teal-700' : 'text-ink-700 hover:bg-ink-100'}`}>
                                            <item.icon className="h-4 w-4" />
                                            {item.label}
                                        </NavLink>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </nav>
                <div className="shrink-0 border-t border-ink-100 bg-white p-3">
                    <div className="flex items-center gap-3 px-2 py-1.5 mb-2">
                        <div className="h-9 w-9 rounded-full bg-brand-teal text-white grid place-items-center font-bold shrink-0">
                            {user?.name?.[0] || 'A'}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate">{user?.name || '—'}</div>
                            <div className="text-[11px] uppercase tracking-wider text-ink-500">{user?.role}</div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={logout}
                        className="w-full flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold
                                   text-ink-700 ring-1 ring-ink-200 hover:bg-rose-50 hover:text-brand-red hover:ring-rose-200 transition"
                    >
                        <LogOut className="h-4 w-4" />
                        Logout
                    </button>
                </div>
            </aside>

            <div className="flex h-screen min-w-0 flex-1 flex-col overflow-hidden">
                <header className="h-16 shrink-0 bg-white border-b border-ink-100 flex items-center justify-between px-4 lg:px-6 z-20">
                    <button className="lg:hidden p-2 -ml-2" onClick={() => setOpenMobile(v => !v)}>
                        {openMobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                    <div className="hidden lg:block text-sm text-ink-500">{variant === 'admin' ? 'Admin Console' : 'Sales Workspace'}</div>
                    <div className="flex items-center gap-3">
                        <NotificationBell />
                        <ProfileDropdown variant={variant} />
                    </div>
                </header>
                {/* Impersonation banner */}
                {isImpersonating() && (
                    <div className="shrink-0 bg-violet-600 text-white px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
                        <div className="flex items-center gap-2 font-medium">
                            <ShieldAlert className="h-4 w-4" />
                            You are viewing the app as <b className="underline underline-offset-2">{user?.name}</b>
                            <span className="text-violet-200 text-xs font-normal">(admin impersonation — session expires in 2 h)</span>
                        </div>
                        <button
                            onClick={stopImpersonation}
                            className="px-3 py-1 rounded-lg bg-white text-violet-700 text-xs font-bold hover:bg-violet-50 transition shrink-0"
                        >
                            Exit &amp; return to admin
                        </button>
                    </div>
                )}
                {/* K3 — view-only banner. Persistent reminder for read-only
                    auditor accounts so they understand why action buttons
                    are missing throughout the app. */}
                {user?.role === 'view_only' && (
                    <div className="shrink-0 bg-slate-700 text-white px-4 py-2 flex items-center gap-2 text-xs">
                        <Eye className="h-4 w-4 shrink-0" />
                        <span className="font-medium">View-only access</span>
                        <span className="text-slate-300">— you can browse every page but cannot create, edit, or delete anything.</span>
                    </div>
                )}

                <main className="flex-1 min-h-0 overflow-y-auto p-4 lg:p-6">
                    <Suspense fallback={<PageLoader />}>
                        <Outlet />
                    </Suspense>
                </main>
            </div>
        </div>
    );
}
